/**
 * Columns a user can pick for a lead export — mirrors the backend's
 * App\Support\LeadExportColumns. "name" is always exported.
 */
export const LEAD_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'job_title', label: 'Job title' },
  { key: 'department', label: 'Department' },
  { key: 'seniority', label: 'Seniority' },
  { key: 'company', label: 'Company' },
  { key: 'domain', label: 'Domain' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'location', label: 'Location' },
  { key: 'company_size', label: 'Company size' },
  { key: 'industry', label: 'Industry' },
  { key: 'icp_score', label: 'ICP score' },
  { key: 'email', label: 'Email' },
  { key: 'generated_at', label: 'Generated at' },
] as const;

export type LeadExportColumn = (typeof LEAD_EXPORT_COLUMNS)[number]['key'];

export const REQUIRED_EXPORT_COLUMN: LeadExportColumn = 'name';

export const ALL_EXPORT_COLUMNS: LeadExportColumn[] = LEAD_EXPORT_COLUMNS.map(c => c.key);

const STORAGE_KEY = 'salesommo:lead-export-columns';

/** Known keys only, in catalog order, always including Name. */
export function normalizeExportColumns(keys: readonly string[]): LeadExportColumn[] {
  const wanted = new Set<string>([...keys, REQUIRED_EXPORT_COLUMN]);
  return ALL_EXPORT_COLUMNS.filter(k => wanted.has(k));
}

/** The user's last pick (per browser); all columns the first time. */
export function loadExportColumns(): LeadExportColumn[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length > 0) {
      return normalizeExportColumns(parsed.map(String));
    }
  } catch {
    /* storage unavailable */
  }
  return [...ALL_EXPORT_COLUMNS];
}

export function saveExportColumns(columns: readonly LeadExportColumn[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    /* storage unavailable */
  }
}
